import {
  IFeeHandlingConfig,
  IFrequency,
  IInvoice,
  IInvoiceAttachments,
  IInvoiceLineItem,
  IInvoiceLineItemsCreateRequest,
  ILateFeeConfig,
  Intervals,
  PaymentMethod
} from "@wingspanhq/payments/dist/interfaces";
import {
  CreditFeeConfig,
  CustomField,
  InvoiceSplit,
  LateFeeConfig,
  LateFeeConfigFrequencyInterval,
  LineItem,
  LineItemType,
  PaymentMethod as PaymentMethodV3,
  RecurrenceSchedule,
  RecurrenceScheduleFrequency,
  RecurringInvoiceSplit,
  RecurringLineItem
} from "@wingspanhq/wingspan-api-sdk";
import { wsMoment as moment } from "@wingspanhq/utils/dist/date/wsMoment";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { quantityToTime, timeToQuantity } from ".";
import {
  getFormDataCustomFields,
  getRequestDataCustomFields
} from "../../shared/components/FormPartialCustomFields";
import { useSetWSStore, useWSStore } from "../../store";
import {
  convertDateToMidday,
  getLocalStartDayByUTC,
  getWSDay,
  isSameDate,
  isToday
} from "../../utils/dates";
import { useHistoryPushWithFilters } from "../../utils/router";
import { updateArray, updateArrayString } from "../../utils/serviceHelper";
import { FormDataCreditFeeHandling } from "../components/InvoicesForm/AdvancedSection";
import { DueSectionValues } from "../components/InvoicesForm/DueSection";
import { FrequencyValues } from "../components/InvoicesForm/FrequencySection";
import { InvoiceSplitValues } from "../components/InvoicesForm/InvoiceSplitsSection";
import { LateFeeValues } from "../components/InvoicesForm/LateFeeSection";
import { FormDataLineItem } from "../components/InvoicesForm/LineItemsSection/types";
import { AttachmentValues } from "../components/InvoicesForm/OtherSection";
import { PaymentMethodsValues } from "../components/InvoicesForm/PaymentMethodsSection";
import { SendSectionValues } from "../components/InvoicesForm/SendSection";
import { LINE_ITEM_HOUR_RATE, LINE_ITEM_UNIT_RATE } from "../constants";

const defaultEndDate = new Date();
defaultEndDate.setMonth(defaultEndDate.getMonth() + 1);
defaultEndDate.setDate(defaultEndDate.getDate() + 1);

export const defaultFrequency: FrequencyValues = {
  every: 1,
  interval: Intervals.Month,
  end: "never",
  endDate: defaultEndDate,
  endAfter: 2,
  start: "onSend",
  startDate: new Date()
};

// Send date

export const generateSendDate = (formValues: SendSectionValues): Date => {
  if (formValues.send.type === "immediately" || isToday(formValues.send.date)) {
    return new Date();
  } else {
    // Convert date to midday if it's in the future, so it's always going to be the same day in utc and user's TZ
    return convertDateToMidday(formValues.send.date);
  }
};

// Due date

export const generateDueDate = (options: {
  formValues: DueSectionValues;
  sendDate: Date;
}): Date => {
  if (
    options.formValues.due === "on-receipt" ||
    (options.formValues.due === "custom" &&
      isToday(options.formValues.customDueDate))
  ) {
    return options.sendDate;
  } else {
    let dueDate;
    if (options.formValues.due === "7-days") {
      dueDate = moment(options.sendDate).add(7, "days").toDate();
    } else if (options.formValues.due === "30-days") {
      dueDate = moment(options.sendDate).add(30, "days").toDate();
    } else {
      dueDate = options.formValues.customDueDate;
    }

    if (isToday(dueDate)) {
      return new Date();
    } else {
      // Convert date to midday if it's in the future, so it's always going to be the same day in utc and user's TZ
      return convertDateToMidday(dueDate);
    }
  }
};

export const generateDefaultDueDateSetByPayer = (options: {
  dueInDays: number;
  sendDate: Date;
}): Date => {
  if (options.dueInDays === 0) {
    // on-receipt
    return options.sendDate;
  } else {
    let dueDate;
    if (options.dueInDays === 7) {
      // 7 days after receipt/open date
      dueDate = moment(options.sendDate).add(7, "days").toDate();
    } else if (options.dueInDays === 15) {
      // 15 days after receipt/open date
      dueDate = moment(options.sendDate).add(15, "days").toDate();
    } else {
      // 30 days after receipt/open date
      dueDate = moment(options.sendDate).add(30, "days").toDate();
    }

    if (isToday(dueDate)) {
      return new Date();
    } else {
      // Convert date to midday if it's in the future, so it's always going to be the same day in utc and user's TZ
      return convertDateToMidday(dueDate);
    }
  }
};

// Late fees

export const generateLateFeeConfig = (data: {
  formValues: LateFeeValues;
  dueDate: Date;
}): ILateFeeConfig | undefined => {
  const every = data.formValues.every;

  const startDate = new Date(data.dueDate);
  if (data.formValues.interval === Intervals.Month) {
    startDate.setMonth(startDate.getMonth() + every);
  } else {
    startDate.setDate(startDate.getDate() + 7 * every);
  }

  return data.formValues.overriden
    ? {
        lateFeeAmount: data.formValues.compoundedAmount,
        frequency: {
          interval: Intervals.Week,
          every: 1,
          dayInInterval:
            data.formValues.interval === Intervals.Week
              ? getWSDay(data.dueDate)
              : data.dueDate.getDate(),
          startDate: moment(data.dueDate).add(1, "day").toDate(),
          endDate: moment(data.dueDate).add(7, "days").toDate()
        }
      }
    : {
        ...(data.formValues.type === "percent"
          ? { lateFeePercentage: data.formValues.amount }
          : {}),
        ...(data.formValues.type === "fixed"
          ? { lateFeeAmount: data.formValues.amount }
          : {}),
        frequency: {
          interval: data.formValues.interval,
          every,
          dayInInterval:
            data.formValues.interval === Intervals.Week
              ? getWSDay(startDate)
              : startDate.getDate(),
          startDate
        }
      };
};

// Recurring frequency

export const convertToLineItemFormDataFromV3 = (
  lineItem: LineItem
): FormDataLineItem => {
  const rate = lineItem.unit
    ? lineItem.unit === LINE_ITEM_HOUR_RATE
      ? "hourly"
      : lineItem.unit === LINE_ITEM_UNIT_RATE
        ? "quantity"
        : "other"
    : "fixed";
  const [hours, minutes] = quantityToTime(lineItem.quantity ?? 0);

  return {
    currency: "USD",
    description: lineItem.description || "",
    totalCost:
      rate === "fixed"
        ? lineItem.totalCost
        : (lineItem.unitCost || 0) * (lineItem.quantity || 0),
    rate,
    costPerUnit: lineItem.unitCost || 0,
    quantity:
      lineItem.unit === LINE_ITEM_HOUR_RATE ? hours : lineItem.quantity || 0,
    minutes: lineItem.unit === LINE_ITEM_HOUR_RATE ? minutes : 0,
    customUnit: rate === "other" ? (lineItem.unit ?? "") : "",
    discount: {
      amount: lineItem.discount?.amount || 0,
      percentage: lineItem.discount?.percentage || 0,
      type: lineItem.discount?.amount ? "amount" : "percentage",
      description: lineItem.discount?.description || ""
    },
    remove: "",
    reimbursableExpense: lineItem.lineItemType === LineItemType.REIMBURSEMENT,
    lineItemType: lineItem.lineItemType
  };
};

/** Seeds the late-fee form section from a v3 `LateFeeConfig`. */
export const lateFeeToFormDataFromV3 = (
  config?: LateFeeConfig
): LateFeeValues => ({
  amount: config?.lateFeeAmount
    ? config.lateFeeAmount
    : config?.lateFeePercentage
      ? config.lateFeePercentage
      : 5,
  type: config?.lateFeeAmount ? "fixed" : "percent",
  every: config?.frequency?.every ? config.frequency.every : 1,
  interval:
    config?.frequency?.interval ===
    LateFeeConfigFrequencyInterval.LATE_FEE_INTERVAL_MONTHLY
      ? Intervals.Month
      : Intervals.Week,
  overriden: false
});

/** Seeds the accepted-payment-methods form section from v3 `PaymentMethod`s. */
export const paymentMethodsToFormDataFromV3 = (
  acceptedPaymentMethods?: PaymentMethodV3[]
): PaymentMethodsValues => ({
  // `Book` (Wingspan balance) is not an option the form offers, so "accept all"
  // keys off the three it does.
  acceptAllPMs: acceptedPaymentMethods
    ? [
        PaymentMethodV3.CREDIT,
        PaymentMethodV3.ACH,
        PaymentMethodV3.MANUAL
      ].every(method => acceptedPaymentMethods.includes(method))
    : true,
  acceptCreditCard:
    acceptedPaymentMethods?.includes(PaymentMethodV3.CREDIT) || false,
  acceptACH: acceptedPaymentMethods?.includes(PaymentMethodV3.ACH) || false,
  acceptManual:
    acceptedPaymentMethods?.includes(PaymentMethodV3.MANUAL) || false
});

/**
 * Seeds the processing-fee form section from a v3 `CreditFeeConfig` — the
 * inverse of what the v3 create body writes (`payeeFeePercentage: 100` when the
 * payee absorbs the fee, otherwise `payerFlatFeePercentage`).
 */
export const creditFeeToFormDataFromV3 = (
  config?: CreditFeeConfig
): FormDataCreditFeeHandling => {
  if (config?.payeeFeePercentage === 100) {
    return {
      clientPaysProcessingFees: false,
      clientProcessingFeePercentage: 2.9
    };
  }

  return {
    clientPaysProcessingFees: true,
    clientProcessingFeePercentage:
      config?.payerFlatFeePercentage || config?.payerFeePercentage || 2.9
  };
};

export const convertToLineItemsCreateRequest = (
  formLineItems: FormDataLineItem[]
): IInvoiceLineItemsCreateRequest[] =>
  formLineItems
    .map(convertToLineItemRequest)
    .filter(item => !!item) as IInvoiceLineItemsCreateRequest[];

export const convertToLineItemsUpdateRequest = (
  formLineItems: FormDataLineItem[]
): Array<IInvoiceLineItemsCreateRequest | null> =>
  formLineItems.map(convertToLineItemRequest);

export const convertToLineItemsUpdateRequest2 = (
  initialLineItems: IInvoiceLineItem[],
  formLineItems: FormDataLineItem[]
): Array<IInvoiceLineItemsCreateRequest | null> =>
  updateArray(initialLineItems, formLineItems.map(convertToLineItemRequest));

export const convertToLineItemRequest = (
  formValues: FormDataLineItem
): IInvoiceLineItemsCreateRequest | null => {
  const integration = formValues.integration;

  const discount =
    formValues.discount &&
    formValues.discount.type === "percentage" &&
    formValues.discount.percentage
      ? {
          percentage: formValues.discount.percentage,
          description: formValues.discount.description
        }
      : formValues.discount &&
          formValues.discount.type === "amount" &&
          formValues.discount.amount
        ? {
            amount: formValues.discount.amount,
            description: formValues.discount.description
          }
        : undefined;

  const labels = formValues.customFields
    ? getRequestDataCustomFields(formValues.customFields)
    : undefined;

  if (formValues.remove) {
    return null;
  } else if (formValues.rate === "fixed") {
    return {
      description: formValues.description,
      totalCost: formValues.totalCost,
      discount,
      integration,
      reimbursableExpense: formValues.reimbursableExpense,
      labels
    };
  } else {
    return {
      description: formValues.description,
      unit:
        formValues.rate === "hourly"
          ? LINE_ITEM_HOUR_RATE
          : formValues.rate === "quantity"
            ? LINE_ITEM_UNIT_RATE
            : formValues.customUnit,
      costPerUnit: formValues.costPerUnit,
      quantity:
        formValues.rate === "hourly"
          ? timeToQuantity(formValues.quantity || 0, formValues.minutes || 0)
          : formValues.quantity,
      discount,
      integration,
      reimbursableExpense: formValues.reimbursableExpense,
      labels
    };
  }
};

// Payment methods

export const paymentMethodsFormDataToCreateRequest = (
  values: PaymentMethodsValues
): Array<PaymentMethod> => {
  if (values.acceptAllPMs) {
    return [PaymentMethod.Credit, PaymentMethod.ACH, PaymentMethod.Manual];
  } else {
    const paymentMethods: PaymentMethod[] = [];

    if (values.acceptCreditCard) {
      paymentMethods.push(PaymentMethod.Credit);
    }
    if (values.acceptACH) {
      paymentMethods.push(PaymentMethod.ACH);
    }
    if (values.acceptManual) {
      paymentMethods.push(PaymentMethod.Manual);
    }

    return paymentMethods;
  }
};

export const paymentMethodsFormDataToUpdateRequest = (
  values: PaymentMethodsValues
): Array<PaymentMethod | null> => {
  if (values.acceptAllPMs) {
    return [PaymentMethod.Credit, PaymentMethod.ACH, PaymentMethod.Manual];
  } else {
    const paymentMethods: Array<PaymentMethod | null> = [];

    paymentMethods.push(values.acceptCreditCard ? PaymentMethod.Credit : null);
    paymentMethods.push(values.acceptACH ? PaymentMethod.ACH : null);
    paymentMethods.push(values.acceptManual ? PaymentMethod.Manual : null);

    return paymentMethods;
  }
};

// Splits

export const invoiceSplitsToFormData = (
  splits?: InvoiceSplit[] | null
): InvoiceSplitValues[] =>
  (splits || []).map(split => ({
    payee: {
      payeeId: split.payeeId,
      payeeEngagementId: split.payeeEngagementId
    },
    description: split.description ?? "",
    amount: split.amount,
    remove: ""
  }));

// Attachments

export const attachmentsToFormData = (
  ids?: Array<string>
): AttachmentValues[] => {
  return (ids || []).map(id => ({ fileId: id }));
};

export const attachmentsFormDataToCreateRequest = (
  formValues?: AttachmentValues[]
): Array<string> => {
  return (formValues || []).filter(a => !a.remove).map(a => a.fileId);
};

export const attachmentsFormDataToUpdateRequest = (
  formValues?: AttachmentValues[],
  oldIds?: IInvoiceAttachments["customAttachmentIds"]
): Array<string | null> => {
  return updateArrayString(
    oldIds || [],
    (formValues || []).map(i => (i.remove ? null : i.fileId))
  );
};

export const useInvoiceFormGoBack = () => {
  const store = useWSStore();
  const setStore = useSetWSStore();
  const location = useLocation<{ backPath?: string }>();
  const historyPushWithFilters = useHistoryPushWithFilters();

  return useCallback(() => {
    historyPushWithFilters(
      store.invoiceFormBackPath ||
        location.state?.backPath ||
        "/member/invoices"
    );
    setStore({ invoiceFormBackPath: undefined });
  }, [
    store.invoiceFormBackPath,
    location.state?.backPath,
    historyPushWithFilters,
    setStore
  ]);
};

export const creditFeeFormDataToRequest = (
  formData: FormDataCreditFeeHandling
): IFeeHandlingConfig =>
  formData?.clientPaysProcessingFees
    ? {
        clientAbsolutePercentage: formData.clientProcessingFeePercentage,
        clientPays: 0,
        memberPays: 0
      }
    : {
        clientAbsolutePercentage: 0,
        clientPays: 0,
        memberPays: 100
      };

export const recurringSplitsToFormData = (
  splits?: RecurringInvoiceSplit[] | null
): InvoiceSplitValues[] =>
  (splits || []).flatMap(split =>
    split.payeeId && split.payeeEngagementId
      ? [
          {
            payee: {
              payeeId: split.payeeId,
              payeeEngagementId: split.payeeEngagementId
            },
            description: split.description ?? "",
            amount: split.amount ?? null,
            remove: ""
          }
        ]
      : []
  );

export const convertToLineItemFormDataFromRecurring = (
  lineItem: RecurringLineItem
): FormDataLineItem =>
  convertToLineItemFormDataFromV3({
    ...lineItem,
    lineItemType: lineItem.type
  } as unknown as LineItem);

export const frequencyToFormDataFromV3 = (
  schedule?: RecurrenceSchedule
): FrequencyValues => {
  const endCondition = schedule?.endCondition as unknown as
    | { type?: string; endDate?: Date; maxOccurrences?: number }
    | undefined;

  return {
    every: schedule?.interval || defaultFrequency.every,
    interval:
      schedule?.frequency === RecurrenceScheduleFrequency.WEEKLY
        ? Intervals.Week
        : Intervals.Month,
    end:
      endCondition?.type === "OnDate"
        ? "date"
        : endCondition?.type === "AfterOccurrences"
          ? "after"
          : "never",
    endDate: endCondition?.endDate
      ? getLocalStartDayByUTC(endCondition.endDate)
      : defaultFrequency.endDate,
    endAfter: endCondition?.maxOccurrences || defaultFrequency.endAfter,
    start: "onSend",
    startDate: schedule?.anchorDate
      ? getLocalStartDayByUTC(schedule.anchorDate)
      : defaultFrequency.startDate
  };
};
